import React, { useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Image from 'components/Image/Image';
import styles from './FullPanel.module.scss';

interface Props {
  image?: {
    src: string;
    name: string;
  };
  title: string;
  description?: string;
  route: string;
  children?: React.ReactNode;
  closeModal?: () => void;
}

const FullPanel: React.FC<Props> = ({ image, title, description, route, children, closeModal }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const defaultCloseModal = (): void => {
    const { pathname } = location;
    const pathnameWithoutHash = pathname.split(`/${route}`)[0];

    navigate(pathnameWithoutHash);
  };

  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  return (
    <div className={styles.Details} data-testid="full-panel" aria-modal="false">
      <button
        className={styles.Close}
        type="button"
        onClick={closeModal || defaultCloseModal}
        data-testid="details-close-button"
        ref={closeRef}
      >
        Close
      </button>
      <section>
        <div className={styles.CornerContainer}>
          <button
            className={styles.CloseButton}
            aria-label="Close Modal"
            data-testid="corner-close-button"
            type="button"
            onClick={closeModal || defaultCloseModal}
            tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
          >
            <i className="mm-times" />
          </button>
        </div>
        <div className={styles.Intro}>
          {image ? (
            <div className={styles.TitleBlock}>
              <Image
                fallbackUrl="/Images/NoApp.png"
                src={image.src}
                alt={`Icon for ${image.name}`}
                className={styles.TitleImage}
              />
              <h3>{title}</h3>
            </div>
          ) : (
            <h3>{title}</h3>
          )}
          {description && description.length > 0 && <p data-testid="full-panel-description">{description}</p>}
        </div>
        <div className={styles.Children}>{children}</div>
      </section>
    </div>
  );
};

export default FullPanel;
